import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Clock, Truck, Package, XCircle, ArrowLeft, Phone } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_INFO = {
  pending_whatsapp: { label: "Pending confirmation", color: "amber", icon: Clock },
  accepted: { label: "Accepted", color: "blue", icon: CheckCircle2 },
  paid: { label: "Paid — being prepared", color: "emerald", icon: CheckCircle2 },
  shipped: { label: "Shipped", color: "indigo", icon: Truck },
  delivered: { label: "Delivered", color: "green", icon: Package },
  cancelled: { label: "Cancelled", color: "rose", icon: XCircle },
};

const CANCELLABLE = new Set(["pending_whatsapp", "accepted", "paid"]);

export const OrderTrackPage = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [phone, setPhone] = useState(params.get("phone") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const fetchOrder = async (e) => {
    if (e) e.preventDefault();
    if (phone.replace(/\D/g, "").length < 4) {
      toast.error("Enter at least the last 4 digits of your phone");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND}/api/orders/${id}`, { params: { phone } });
      setOrder(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not load order";
      toast.error(msg);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const cancelOrder = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${BACKEND}/api/orders/${id}/cancel`, { phone });
      const refundMsg = {
        auto_processed: "Refund initiated (5-7 business days to your source of payment).",
        manual_required: "Refund will be processed manually within 3 business days.",
        not_applicable: "No refund needed (Cash on Delivery).",
      }[res.data.refund_status] || "";
      toast.success(`Order cancelled. ${refundMsg}`);
      setShowCancelConfirm(false);
      await fetchOrder();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel order");
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => {
    if (phone && !order) { fetchOrder(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 pb-24 md:pb-16" data-testid="order-track-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#8E6377] hover:text-[#D81B60] mb-4">
        <ArrowLeft size={16} /> Back home
      </Link>

      <div className="bg-white rounded-3xl border border-[#F4D8E2] p-6 shadow-[0_10px_40px_rgba(216,27,96,0.08)]">
        <p className="text-xs uppercase tracking-[0.2em] text-[#D81B60] font-bold">Order Tracking</p>
        <h1 className="font-serif text-3xl text-[#2D0A22] mt-1">Order #{id.slice(0, 8).toUpperCase()}</h1>

        {!order && (
          <form onSubmit={fetchOrder} className="mt-6 space-y-3" data-testid="order-lookup-form">
            <p className="text-sm text-[#8E6377]">Enter the phone number used to place this order (last 4 digits are enough).</p>
            <div className="flex items-center gap-2 border border-[#F4D8E2] rounded-xl px-3 py-2 bg-[#FDF2F5]">
              <Phone size={16} className="text-[#D81B60]" />
              <input
                autoFocus
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Last 4 digits or full number"
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="order-lookup-phone"
              />
            </div>
            <button type="submit" disabled={loading} className="w-full pill-btn" data-testid="order-lookup-submit">
              {loading ? "Looking up..." : "View my order"}
            </button>
          </form>
        )}

        {order && (
          <div className="mt-6 space-y-5" data-testid="order-details">
            <StatusBadge status={order.status} />

            <div className="rounded-2xl bg-[#FDF2F5] border border-[#F4D8E2] p-4 space-y-1 text-sm">
              <p><b className="text-[#2D0A22]">Name:</b> {order.customer_name}</p>
              <p><b className="text-[#2D0A22]">Phone:</b> {order.phone_masked}</p>
              <p><b className="text-[#2D0A22]">Address:</b> {order.address}</p>
              <p><b className="text-[#2D0A22]">Total:</b> ₹{Number(order.total).toLocaleString("en-IN")} <span className="text-xs text-[#8E6377]">({(order.payment_method || "cod").toUpperCase()})</span></p>
            </div>

            <div>
              <p className="text-xs font-semibold text-[#2D0A22] mb-2">ITEMS ({order.items?.length || 0})</p>
              <ul className="space-y-1 text-sm">
                {(order.items || []).map((it, i) => (
                  <li key={i} className="flex justify-between text-[#2D0A22]">
                    <span>{it.name} × {it.quantity}</span>
                    <span>₹{Number(it.price * it.quantity).toLocaleString("en-IN")}</span>
                  </li>
                ))}
              </ul>
            </div>

            {order.status === "cancelled" && (
              <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-800">
                <p className="font-semibold">This order has been cancelled.</p>
                {order.refund_status === "auto_processed" && <p className="mt-1">✓ Refund initiated automatically. It should reflect in 5-7 business days.</p>}
                {order.refund_status === "manual_required" && <p className="mt-1">Refund will be processed manually within 3 business days. Our team will reach out on WhatsApp.</p>}
                {order.refund_status === "not_applicable" && <p className="mt-1">This was a Cash-on-Delivery order — no refund needed.</p>}
              </div>
            )}

            {CANCELLABLE.has(order.status) && (
              <div className="pt-2 border-t border-[#F4D8E2]">
                {!showCancelConfirm ? (
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    data-testid="cancel-order-button"
                    className="w-full py-3 rounded-full border border-rose-300 text-rose-700 font-semibold hover:bg-rose-50 transition-colors"
                  >
                    Cancel this order
                  </button>
                ) : (
                  <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 space-y-3">
                    <p className="text-sm text-rose-900">Are you sure? Cancellation is only possible before your order is dispatched. For prepaid orders, a refund will be initiated automatically.</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowCancelConfirm(false)}
                        disabled={cancelling}
                        className="flex-1 py-2 rounded-full border border-[#F4D8E2] text-[#2D0A22] font-semibold"
                      >
                        Keep order
                      </button>
                      <button
                        onClick={cancelOrder}
                        disabled={cancelling}
                        data-testid="confirm-cancel-button"
                        className="flex-1 py-2 rounded-full bg-rose-600 text-white font-semibold hover:bg-rose-700"
                      >
                        {cancelling ? "Cancelling..." : "Yes, cancel"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!CANCELLABLE.has(order.status) && order.status !== "cancelled" && (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                Your order has already been {order.status}. Per our policy, cancellations are only accepted before dispatch. See our <Link to="/policies" className="underline font-semibold">refund &amp; return policy</Link>.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const info = STATUS_INFO[status] || { label: status, color: "gray", icon: Clock };
  const Icon = info.icon;
  const cmap = {
    amber: "bg-amber-100 text-amber-800 border-amber-200",
    blue: "bg-blue-100 text-blue-800 border-blue-200",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    indigo: "bg-indigo-100 text-indigo-800 border-indigo-200",
    green: "bg-green-100 text-green-800 border-green-200",
    rose: "bg-rose-100 text-rose-800 border-rose-200",
    gray: "bg-gray-100 text-gray-800 border-gray-200",
  }[info.color] || "bg-gray-100 text-gray-800 border-gray-200";
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-semibold text-sm ${cmap}`} data-testid="order-status-badge">
      <Icon size={16} /> {info.label}
    </div>
  );
};
